import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { LoaderCircle, PartyPopper, XCircle } from "lucide-react";
import { Logo } from "../components/landing/Logo";
import { t, useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Landing page after Stripe Checkout redirects back. Polls until the payment
// confirms server-side, then points the user at their wallet/timeline.
export default function PaymentResult({ outcome }) {
  const [params] = useSearchParams();
  const lang = useLang();
  const [state, setState] = useState(outcome === "success" ? "checking" : "cancelled");
  const [orderId, setOrderId] = useState("");

  useEffect(() => {
    if (outcome !== "success") return;
    const sid = params.get("session_id");
    if (!sid) return setState("unknown");
    let tries = 0;
    const poll = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await axios.get(`${API}/payments/stripe/status/${sid}`);
        if (data.payment_status === "paid") {
          setOrderId(data.order_id || "");
          setState("paid");
          clearInterval(poll);
        }
      } catch {
        /* keep polling */
      }
      if (tries > 12) {
        setState("timeout");
        clearInterval(poll);
      }
    }, 2000);
    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const content = {
    checking: { icon: <LoaderCircle className="h-12 w-12 animate-spin text-[#06B6D4]" />, title: "Confirming payment…", sub: "" },
    paid: { icon: <PartyPopper className="h-12 w-12 text-[#22C55E]" />, title: t(lang, "pay_success_title"), sub: t(lang, "pay_success_sub") },
    timeout: { icon: <LoaderCircle className="h-12 w-12 text-[#F97316]" />, title: t(lang, "pay_success_title"), sub: t(lang, "track_in_wallet") },
    unknown: { icon: <XCircle className="h-12 w-12 text-[#F97316]" />, title: t(lang, "pay_cancel_title"), sub: t(lang, "pay_cancel_sub") },
    cancelled: { icon: <XCircle className="h-12 w-12 text-[#F97316]" />, title: t(lang, "pay_cancel_title"), sub: t(lang, "pay_cancel_sub") },
  }[state];

  return (
    <div data-testid={`payment-${outcome}`} dir={lang === "ar" ? "rtl" : "ltr"} className="flex min-h-screen flex-col items-center justify-center bg-[#09090B] px-6 text-center text-white">
      <Logo className="h-12 w-12" />
      <div className="mt-8">{content.icon}</div>
      <h1 className="mt-5 font-display text-3xl font-extrabold" data-testid="payment-result-title">{content.title}</h1>
      {orderId && <p className="mt-2 font-jbmono text-sm text-[#06B6D4]" data-testid="payment-order-id">#{orderId.slice(-6)}</p>}
      {content.sub && <p className="mt-2 text-sm text-slate-400">{content.sub}</p>}
      <Link
        to="/app"
        data-testid="payment-back"
        className="mt-8 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-8 py-3.5 font-display text-xs font-bold uppercase tracking-wider text-white"
      >
        {t(lang, "back_to_app")}
      </Link>
    </div>
  );
}
